import { plural } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { observer } from "mobx-react-lite";
import { useState } from "react";

import { PermissionBit } from "../../../api";
import { AppModel } from "../../../app/app-model";
import { QuickLookButton } from "../../../components/quick-look/quick-look-button";
import { AddButton } from "../../../components/ui-kit/add-button";
import { Button } from "../../../components/ui-kit/button";
import { CellLastEdit } from "../../../components/ui-kit/tables/cells/cell-last-edit";
import { CellText } from "../../../components/ui-kit/tables/cells/cell-text";
import { DataTable } from "../../../components/ui-kit/tables/data-table";
import {
  ppgConditionsText,
  ppgRelatedFlag,
} from "../../../constants/enum-to-text";
import {
  ppgConditionValueOptions,
  uniqueArrayValueOptions,
  uniqueValueOptions,
} from "../../../utils/filter-presets";
import { useFetch } from "../../../utils/use-fetch";
import { assert } from "../../../utils/utils";
import {
  MedDataSource,
  MergedMedData,
  MergedMedDataItem,
} from "../common/merged-med-data";
import { PatientSubpage } from "../common/patient-subpage";
import { PlotCanvas, PlotControls, usePlot } from "../common/plot";
import { AddVitalsDialog } from "../medical/add-vitals-dialog";
import { ManualValueOverrideDialog } from "./manual-value-override-dialog";
import { VitalsPlotModel } from "./vitals-medical-plot";

export const VitalsTable = observer(function VitalsTable(props: {
  app: AppModel;
}) {
  const { t } = useLingui();
  const patient = props.app.requirePatient();
  const api = props.app.seerlinqApi;
  const [model] = useState(
    () => new MergedMedData([], api.permissions, api.requireUserUUID()),
  );
  const [overrideValueItem, setOverrideValueItem] =
    useState<MergedMedDataItem>();
  const [showAddDialog, setShowAddDialog] = useState(false);

  const { loading, refetch } = useFetch(
    async ({ isRefetch }) => {
      await patient.fetchVitals(model, isRefetch);
    },
    [patient.patientId],
  );

  const plot = usePlot(
    () =>
      new VitalsPlotModel(
        model,
        [
          "heart rate",
          "blood pressure diastolic",
          "blood pressure systolic",
          "SpO2",
        ],
        "plot-canvas",
      ),
  );

  return (
    <PatientSubpage title={t`Vitals`} loading={loading}>
      <PlotCanvas model={plot} />

      <DataTable
        filterActions={
          <>
            <AddButton onClick={() => setShowAddDialog(true)}>
              <Trans>Add vitals</Trans>
            </AddButton>
            <PlotControls model={plot} />
          </>
        }
        persistState
        model={model}
        editMode="dialog"
        itemKeyField="uuid"
        filterNoun={(count) =>
          t`${plural(count, { one: "vital", other: "vitals" })}`
        }
        columns={[
          {
            header: <Trans>Measured</Trans>,
            attr: "measurement_datetime",
            type: "datetime",
            editable: (item) =>
              item.data_source === MedDataSource.PatientOrPhysician,
            sort: true,
            initSort: "desc",
          },
          {
            header: <Trans>Source</Trans>,
            attr: "data_source",
            type: "string",
            filter: {
              kind: "multi",
              group: t`Vitals`,
              options: uniqueValueOptions("data_source"),
            },
          },
          {
            header: <Trans>Type</Trans>,
            attr: "measurement_type",
            type: "string",
            filter: {
              kind: "multi",
              group: t`Vitals`,
              options: uniqueValueOptions("measurement_type"),
            },
          },
          {
            header: <Trans>Value</Trans>,
            attr: "measurement_value",
            type: "number",
            precision: 1,
            editable: (item) =>
              item.data_source === MedDataSource.PatientOrPhysician,
          },
          {
            header: <Trans>Unit</Trans>,
            attr: "measurement_unit",
            type: "string",
          },
          {
            header: <Trans>Manual value</Trans>,
            attr: "override_value",
            type: "number",
            precision: 1,
            editable: (item) =>
              item.data_source !== MedDataSource.PatientOrPhysician &&
              item.override_value !== null,
          },
          {
            header: <Trans>Override value</Trans>,
            cell: ({ item }) =>
              item.data_source !== MedDataSource.PatientOrPhysician ? (
                <Button
                  onClick={() => {
                    setOverrideValueItem(item);
                  }}
                  variant="subtle"
                >
                  <Trans>Override</Trans>
                </Button>
              ) : (
                "--"
              ),
          },
          {
            header: <Trans>Quality Flag</Trans>,
            attr: "seerlinq_measurement_quality_flag",
            type: "enum",
            values: ppgRelatedFlag,
            filter: {
              kind: "multi",
              group: t`Vitals`,
              options: new Map(Object.entries(ppgRelatedFlag)),
            },
            editable: (item) =>
              item.data_source !== MedDataSource.PatientOrPhysician,
          },
          {
            header: <Trans>PPG device</Trans>,
            attr: "ppgDevice",
            type: "string",
            filter: {
              kind: "multi",
              group: t`Vitals`,
              options: uniqueValueOptions("ppgDevice"),
            },
          },
          {
            header: <Trans>PPG condition</Trans>,
            attr: "ppgConditions",
            type: "string",
            // Same rendering `type: "string"` would give, with the raw
            // conditions swapped for their display text.
            cell: ({ item }) => (
              <CellText
                item={item}
                attr="ppgConditions"
                displayValue={ppgConditionsText(item.ppgConditions)}
              />
            ),
            filter: {
              kind: "multi",
              group: t`Vitals`,
              options: ppgConditionValueOptions("ppgConditions"),
            },
          },
          {
            header: <Trans>Tags</Trans>,
            attr: "tags",
            type: "array",
            filter: {
              kind: "multi",
              group: t`Vitals`,
              options: uniqueArrayValueOptions("tags"),
            },
          },
          {
            header: <Trans>Comment</Trans>,
            attr: "comment",
            type: "string",
            multiline: 3,
            editable: true,
            width: 200,
          },
          {
            header: <Trans>Basic QuickLook</Trans>,
            requiredPermission: PermissionBit.PPG_RAW,
            sticky: "right",
            width: 100,
            cell: ({ item }) =>
              item.ppgQuickLook ? (
                <QuickLookButton
                  label={t`QuickLook`}
                  resultLabel={t`View QuickLook`}
                  model={item.ppgQuickLook}
                  size="small"
                />
              ) : (
                "--"
              ),
          },
          model.showingHistory && {
            header: <Trans>Added</Trans>,
            attr: "created_at",
            type: "datetime",
          },
          model.showingHistory && {
            header: <Trans>Last edit</Trans>,
            cell: ({ item }) => <CellLastEdit item={item} />,
          },
        ]}
        onEdit={async (editedField, item) => {
          assert(item.data_source, "row is missing its data_source");
          await patient.updateItem(
            model.updateEndpoint[item.data_source],
            item.uuid,
            editedField,
          );
          await refetch();
        }}
        onDelete={async (item) => {
          await patient.deleteItem("medicaldata", item.uuid);
          await refetch();
        }}
      />

      {overrideValueItem && (
        <ManualValueOverrideDialog
          item={overrideValueItem}
          onClose={() => {
            setOverrideValueItem(undefined);
          }}
          onSubmit={async (value) => {
            assert(
              overrideValueItem.data_source,
              "row is missing its data_source",
            );
            await patient.overrideValue(
              value,
              overrideValueItem.uuid,
              overrideValueItem.data_source,
            );
            setOverrideValueItem(undefined);
            await refetch();
          }}
        />
      )}

      {showAddDialog && (
        <AddVitalsDialog
          app={props.app}
          variant="vitals"
          onClose={() => setShowAddDialog(false)}
          onSaved={refetch}
        />
      )}
    </PatientSubpage>
  );
});
